import { Link, router } from '@inertiajs/react'
import InfoTip from '../../../components/InfoTip'

// ─── Provider metadata ─────────────────────────────────────────────────────────

const PROVIDERS = [
  { key: 'cloudtraq', label: 'cloudTraQ',       icon: '🛰',  color: '#2196F3' },
  { key: 'sabre',     label: 'Sabre',            icon: '✈',  color: '#003087' },
  { key: 'ibm',       label: 'IBM Orchestrate',  icon: '🧠',  color: '#0530AD' },
  { key: 'hilton',    label: 'Hilton CRS',        icon: '🏨',  color: '#004B87' },
  { key: 'national',  label: 'National IDS',      icon: '🚗',  color: '#E60000' },
]

// ─── Auth gate ────────────────────────────────────────────────────────────────

function Unauthorized({ providerLabel }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <div className="text-5xl mb-4">🔒</div>
      <p className="text-lg font-semibold text-gray-700">Access Restricted</p>
      <p className="text-sm text-gray-500 mt-2 max-w-xs">
        Your operator account is not authorized for the{' '}
        <strong>{providerLabel}</strong> dashboard. Contact cloudTraQ admin to request access.
      </p>
    </div>
  )
}

// ─── cloudTraQ panel ──────────────────────────────────────────────────────────

function CloudTraQPanel({ panel }) {
  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
        <p className="font-semibold text-blue-800 mb-2">🛰 cloudTraQ Ops Center</p>
        <p className="text-blue-700 text-xs mb-3">
          Real-time GDS ops monitoring across all carriers, IBM ADK skills, and passenger dispatch.
        </p>
        <div className="flex flex-wrap gap-2">
          <Link href="/admin/cloudtraq_gds" className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded hover:bg-blue-700">
            Command Center
          </Link>
          <Link href="/admin/cloudtraq_gds/skill_feed" className="px-3 py-1.5 bg-white border border-blue-300 text-blue-700 text-xs rounded hover:bg-blue-50">
            Skill Feed
          </Link>
          <Link href="/admin/cloudtraq_gds/high_risk" className="px-3 py-1.5 bg-white border border-blue-300 text-blue-700 text-xs rounded hover:bg-blue-50">
            High Risk Candidates
          </Link>
          <Link href="/admin/cloudtraq_gds/dispatch_board" className="px-3 py-1.5 bg-white border border-blue-300 text-blue-700 text-xs rounded hover:bg-blue-50">
            Dispatch Board
          </Link>
          <Link href="/admin/cloudtraq_gds/carrier_metrics" className="px-3 py-1.5 bg-white border border-blue-300 text-blue-700 text-xs rounded hover:bg-blue-50">
            Carrier Metrics
          </Link>
        </div>
      </div>

      {panel?.stats && (
        <div className="grid grid-cols-3 gap-3">
          {Object.entries(panel.stats).map(([k, v]) => (
            <div key={k} className="bg-white border border-gray-200 rounded-lg p-3 text-center">
              <p className="text-xl font-bold tabular-nums text-gray-800">{v}</p>
              <p className="text-xs text-gray-500 capitalize">{k.replace(/_/g, ' ')}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Sabre panel ──────────────────────────────────────────────────────────────

function SabrePanel({ panel }) {
  const iframeUrl = panel?.dashboard_url
  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg p-4 text-xs">
        <p className="font-semibold text-gray-700 mb-2">✈ Sabre GDS — PCC Session Summary</p>
        {panel?.pcc ? (
          <div className="grid grid-cols-3 gap-3">
            {[
              ['PCC',         panel.pcc],
              ['Active PNRs', panel.active_pnrs],
              ['GDS Source',  panel.gds_source || 'Sabre-1SVARG'],
              ['Queue Count', panel.queue_count],
              ['Last Sync',   panel.last_sync_at
                ? new Date(panel.last_sync_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                : '—'],
            ].filter(([, v]) => v != null).map(([label, value]) => (
              <div key={label}>
                <p className="text-gray-400">{label}</p>
                <p className="font-semibold text-gray-800">{value}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400 italic">No Sabre PCC session data available.</p>
        )}
        <div className="mt-3 flex gap-2 flex-wrap">
          <Link href="/admin/cloudtraq_gds/skill_feed" className="text-xs px-3 py-1.5 bg-gray-50 border border-gray-200 text-gray-700 rounded hover:bg-gray-100">
            View ADK Skill Feed →
          </Link>
          <Link href="/admin/cloudtraq_gds/pnr_mutations" className="text-xs px-3 py-1.5 bg-gray-50 border border-gray-200 text-gray-700 rounded hover:bg-gray-100">
            PNR Mutations →
          </Link>
        </div>
      </div>

      {/* Embedded Sabre portal iframe if dashboard_url configured */}
      {iframeUrl && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 border-b border-gray-200 px-3 py-2 flex items-center justify-between text-xs">
            <span className="font-semibold text-gray-600">Sabre Agency360 / SynXis Portal</span>
            <a href={iframeUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
              Open in new tab ↗
            </a>
          </div>
          <iframe
            src={iframeUrl}
            title="Sabre Portal"
            className="w-full"
            style={{ height: 500, border: 'none' }}
            sandbox="allow-same-origin allow-scripts allow-forms"
          />
        </div>
      )}
    </div>
  )
}

// ─── IBM panel ────────────────────────────────────────────────────────────────

function IBMPanel({ panel }) {
  const SKILL_COLORS = {
    pnr_ingest: '#2196F3', ava_insights: '#9C27B0', irop_event_push: '#F44336',
    msp_dispatch: '#FF9800', vuuoffer_push: '#00BCD4', attestation_push: '#4CAF50', dispatch_status: '#607D8B',
  }
  const iframeUrl = panel?.dashboard_url
  const skillStats = panel?.skill_stats || {}

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <p className="text-xs font-semibold text-gray-700 mb-3">🧠 IBM watsonx ADK — Skill Call Stats (1h)</p>
        {Object.keys(skillStats).length === 0 ? (
          <p className="text-xs text-gray-400 italic">No IBM ADK activity in the last hour.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {Object.entries(skillStats).map(([skill, count]) => (
              <div
                key={skill}
                className="px-3 py-2 rounded border text-xs"
                style={{ borderColor: (SKILL_COLORS[skill] || '#9E9E9E') + '44', background: (SKILL_COLORS[skill] || '#9E9E9E') + '0D' }}
              >
                <p className="font-bold" style={{ color: SKILL_COLORS[skill] || '#9E9E9E' }}>{skill}</p>
                <p className="text-2xl font-bold tabular-nums text-gray-700">{count}</p>
                <p className="text-gray-400">calls</p>
              </div>
            ))}
          </div>
        )}
        <div className="mt-3 flex gap-2 flex-wrap">
          <Link href="/admin/cloudtraq_gds/skill_feed" className="text-xs px-3 py-1.5 bg-indigo-50 border border-indigo-200 text-indigo-700 rounded hover:bg-indigo-100">
            Full Skill Feed →
          </Link>
        </div>
      </div>

      {iframeUrl && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 border-b border-gray-200 px-3 py-2 flex items-center justify-between text-xs">
            <span className="font-semibold text-gray-600">IBM Cloud Pak / Orchestrate Dashboard</span>
            <a href={iframeUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
              Open in new tab ↗
            </a>
          </div>
          <iframe
            src={iframeUrl}
            title="IBM Portal"
            className="w-full"
            style={{ height: 500, border: 'none' }}
            sandbox="allow-same-origin allow-scripts allow-forms"
          />
        </div>
      )}

      {!iframeUrl && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 text-xs text-indigo-700">
          <p className="font-semibold mb-1">IBM Cloud Pak Portal</p>
          <p>Configure <code>OperatorTenant.branding.dashboard_url</code> for the ibm tenant to enable the embedded portal view.</p>
        </div>
      )}
    </div>
  )
}

// ─── Hilton panel ─────────────────────────────────────────────────────────────

function HiltonPanel({ panel }) {
  const iframeUrl = panel?.dashboard_url
  const reservations = panel?.active_reservations || []

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg p-4 text-xs">
        <p className="font-semibold text-gray-700 mb-3">🏨 Hilton CRS — Active Reservations</p>
        {reservations.length === 0 ? (
          <p className="text-gray-400 italic">No active Hilton hotel reservations in current PNR scope.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="py-1 pr-3 text-left text-gray-400 font-semibold">CRS PNR</th>
                  <th className="py-1 pr-3 text-left text-gray-400 font-semibold">Property</th>
                  <th className="py-1 pr-3 text-left text-gray-400 font-semibold">Room</th>
                  <th className="py-1 pr-3 text-left text-gray-400 font-semibold">Check-in</th>
                  <th className="py-1 text-left text-gray-400 font-semibold">Itinerary</th>
                </tr>
              </thead>
              <tbody>
                {reservations.map((r, i) => (
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-1.5 pr-3 font-mono font-bold text-purple-700">{r.crs_pnr || '—'}</td>
                    <td className="py-1.5 pr-3 text-gray-600">{r.property_code || '—'}</td>
                    <td className="py-1.5 pr-3 text-gray-600">{r.room_type || '—'}</td>
                    <td className="py-1.5 pr-3 text-gray-600">{r.check_in || '—'}</td>
                    <td className="py-1.5">
                      {r.itinerary_ref
                        ? <a href={`/admin/cloudtraq_gds/itinerary/${r.itinerary_ref}`} className="text-teal-600 hover:underline">{r.itinerary_ref}</a>
                        : <span className="text-gray-400">—</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {iframeUrl && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 border-b border-gray-200 px-3 py-2 flex items-center justify-between text-xs">
            <span className="font-semibold text-gray-600">Hilton Property Management Portal</span>
            <a href={iframeUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
              Open in new tab ↗
            </a>
          </div>
          <iframe
            src={iframeUrl}
            title="Hilton Portal"
            className="w-full"
            style={{ height: 500, border: 'none' }}
            sandbox="allow-same-origin allow-scripts allow-forms"
          />
        </div>
      )}
    </div>
  )
}

// ─── National IDS panel ───────────────────────────────────────────────────────

function NationalPanel({ panel }) {
  const iframeUrl = panel?.dashboard_url
  const reservations = panel?.active_reservations || []

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg p-4 text-xs">
        <p className="font-semibold text-gray-700 mb-3">🚗 National IDS — Active Car Reservations</p>
        {reservations.length === 0 ? (
          <p className="text-gray-400 italic">No active National car reservations in current PNR scope.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="py-1 pr-3 text-left text-gray-400 font-semibold">IDS PNR</th>
                  <th className="py-1 pr-3 text-left text-gray-400 font-semibold">Pickup IATA</th>
                  <th className="py-1 pr-3 text-left text-gray-400 font-semibold">Vehicle</th>
                  <th className="py-1 pr-3 text-left text-gray-400 font-semibold">Pickup Time</th>
                  <th className="py-1 text-left text-gray-400 font-semibold">Itinerary</th>
                </tr>
              </thead>
              <tbody>
                {reservations.map((r, i) => (
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-1.5 pr-3 font-mono font-bold text-orange-700">{r.ids_pnr || '—'}</td>
                    <td className="py-1.5 pr-3 text-gray-600 font-mono">{r.pickup_iata || '—'}</td>
                    <td className="py-1.5 pr-3 text-gray-600">{r.vehicle_class || '—'}</td>
                    <td className="py-1.5 pr-3 text-gray-600">{r.pickup_dt
                      ? new Date(r.pickup_dt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                      : '—'
                    }</td>
                    <td className="py-1.5">
                      {r.itinerary_ref
                        ? <a href={`/admin/cloudtraq_gds/itinerary/${r.itinerary_ref}`} className="text-teal-600 hover:underline">{r.itinerary_ref}</a>
                        : <span className="text-gray-400">—</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {iframeUrl && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 border-b border-gray-200 px-3 py-2 flex items-center justify-between text-xs">
            <span className="font-semibold text-gray-600">National IDS Portal</span>
            <a href={iframeUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
              Open in new tab ↗
            </a>
          </div>
          <iframe
            src={iframeUrl}
            title="National IDS Portal"
            className="w-full"
            style={{ height: 500, border: 'none' }}
            sandbox="allow-same-origin allow-scripts allow-forms"
          />
        </div>
      )}
    </div>
  )
}

// ─── Panel router ─────────────────────────────────────────────────────────────

function PanelContent({ providerKey, authorized, panel }) {
  if (!authorized) {
    const meta = PROVIDERS.find(p => p.key === providerKey)
    return <Unauthorized providerLabel={meta?.label || providerKey} />
  }
  switch (providerKey) {
    case 'cloudtraq': return <CloudTraQPanel panel={panel} />
    case 'sabre':     return <SabrePanel panel={panel} />
    case 'ibm':       return <IBMPanel panel={panel} />
    case 'hilton':    return <HiltonPanel panel={panel} />
    case 'national':  return <NationalPanel panel={panel} />
    default: return (
      <div className="py-12 text-center text-gray-400 text-sm">
        Panel for <strong>{providerKey}</strong> is not configured.
      </div>
    )
  }
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ThirdPartyPanel({ provider, authorized, panel, tenant_branding }) {
  const current = PROVIDERS.find(p => p.key === provider) || PROVIDERS[0]

  function switchTo(providerKey) {
    router.get(`/admin/cloudtraq_gds/third_party/${providerKey}`, {}, { preserveScroll: true })
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Provider Dashboards</h1>
            <p className="text-sm text-gray-500 mt-1">
              Authorized 3rd-party management systems · tenant: {tenant_branding?.tenant_key || 'cloudtraq'}
            </p>
          </div>
          <Link href="/admin/cloudtraq_gds" className="text-sm text-blue-600 hover:underline">← Command Center</Link>
        </div>

        {/* Tab bar */}
        <div className="flex items-center gap-1 mb-4 bg-white border border-gray-200 rounded-xl p-1.5 overflow-x-auto">
          {PROVIDERS.map(p => {
            const isActive = p.key === provider
            return (
              <button
                key={p.key}
                onClick={() => switchTo(p.key)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex-shrink-0 ${
                  isActive
                    ? 'text-white shadow-sm'
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
                style={isActive ? { background: p.color } : {}}
              >
                <span>{p.icon}</span>
                {p.label}
              </button>
            )
          })}
        </div>

        {/* Active provider header */}
        <div className="flex items-center gap-3 mb-4">
          <span className="text-2xl">{current.icon}</span>
          <div>
            <h2 className="text-lg font-bold" style={{ color: current.color }}>{current.label}</h2>
            {authorized && (
              <span className="text-xs text-green-600 font-medium">✓ Authorized</span>
            )}
            {!authorized && (
              <span className="text-xs text-red-500 font-medium">✗ Not Authorized</span>
            )}
          </div>
        </div>

        {/* Panel content */}
        <PanelContent providerKey={provider} authorized={authorized} panel={panel} />

        {/* Security notice */}
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-700">
          <span className="font-semibold">🔒 Tenant isolation active.</span>{' '}
          Data shown is scoped exclusively to the{' '}
          <strong>{tenant_branding?.tenant_key || 'cloudtraq'}</strong> operator tenant.
          Provider data does not cross tenant boundaries.
          See <code>CLOUDTRAQ_GDS_OPS_ARCHITECTURE.md §4.2</code> for isolation spec.
        </div>

        {/* Nav */}
        <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-500">
          <Link href="/admin/cloudtraq_gds" className="text-blue-600 hover:underline">← Command Center</Link>
          <Link href="/admin/cloudtraq_gds/carrier_metrics" className="text-blue-600 hover:underline">Carrier Metrics</Link>
          <Link href="/admin/cloudtraq_gds/pnr_mutations" className="text-blue-600 hover:underline">PNR Mutations</Link>
        </div>
      </div>
    </div>
  )
}
